import { useEffect, useState } from 'react';

import { de } from '../../i18n/de';
import {
  acknowledgeBackupSecrets,
  backupCredentials,
  mintBackupSecrets,
  type MintedSecrets,
} from '../../core/backup/credentials';
import { useBackup } from '../../core/backup/BackupService';
import { CareButton } from './BetreuerModus';

/**
 * Setting up the cloud backup's two secrets, and showing them exactly once.
 *
 * This is a small screen carrying the whole weight of Phase 4's honesty. The six words are not recoverable
 * — not by a caregiver, not by a support call, not by us — so this is the only moment they exist anywhere
 * except in the person's household. Three consequences shape it:
 *
 *  1. **Nothing is minted until somebody asks.** No secrets appear on a device by surprise; the act is
 *     explicit and a human is present for it.
 *  2. **The warning is not softened.** "Bitte gut aufbewahren" produces a screenshot in a photo library.
 *     Stating that the backups become unreadable produces a piece of paper, which is what is needed.
 *  3. **Displayed once, and the code cannot show them again** — the passphrase is never stored, only the
 *     salt and a non-extractable key. Leaving the words retrievable from care mode would have made this
 *     screen a nicer experience and the passphrase a much weaker secret.
 *
 * The acknowledgement is a real gate: until somebody confirms they have written it down, the secrets stay
 * on screen. Care mode is entered deliberately by a caregiver, so nothing here is in front of the person.
 */
export function BackupSecrets(): React.JSX.Element {
  const [existing, setExisting] = useState<'unknown' | 'absent' | 'present'>('unknown');
  const [shown, setShown] = useState<MintedSecrets | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void backupCredentials().then((found) => setExisting(found ? 'present' : 'absent'));
  }, []);

  const mint = async (): Promise<void> => {
    setBusy(true);
    try {
      setShown(await mintBackupSecrets());
      setExisting('present');
      // The status panel above mounted before these credentials existed, so it still believes backup is not
      // set up — and without this, `Jetzt sichern` would not appear until something else happened to refresh
      // it. Found by a flow test where the first automatic attempt failed: the button a caregiver needs in
      // exactly that situation was the button that was missing.
      await useBackup.getState().hydrate();
    } finally {
      setBusy(false);
    }
  };

  const acknowledge = async (): Promise<void> => {
    await acknowledgeBackupSecrets();
    // Cleared from React state as well as from the screen: the words should not sit in a component that
    // a later render could put back.
    setShown(null);
  };

  return (
    <section
      data-testid="care-backup-secrets"
      style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 'var(--font-body)' }}
    >
      <h2 style={{ fontWeight: 700 }}>{de.backupSecretsTitle}</h2>

      {shown ? (
        <div
          data-testid="care-backup-secrets-shown"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: 8,
            border: '3px solid var(--danger)',
            borderRadius: 'var(--radius)',
          }}
        >
          <p>{de.backupSecretsIntro}</p>
          <p style={{ fontWeight: 700 }}>{de.backupSecretsWarning}</p>

          <p>{de.backupPassphraseLabel}</p>
          {/* Monospace and spaced out: this gets copied by hand onto a card, so the reading has to be
              unambiguous rather than pretty. */}
          <p
            data-testid="care-backup-passphrase"
            style={{ fontFamily: 'ui-monospace, monospace', fontSize: 'var(--font-button)', lineHeight: 1.6 }}
          >
            {shown.passphrase}
          </p>

          <p>{de.backupTokenLabel}</p>
          <p
            data-testid="care-backup-token"
            style={{ fontFamily: 'ui-monospace, monospace', overflowWrap: 'anywhere' }}
          >
            {shown.token}
          </p>

          <CareButton
            id="care-backup:acknowledge"
            label={de.backupSecretsAcknowledge}
            onPress={() => void acknowledge()}
          />
        </div>
      ) : existing === 'present' ? (
        <p data-testid="care-backup-secrets-set">{de.backupSecretsStored}</p>
      ) : existing === 'absent' ? (
        <>
          {/* Below the adopt field on purpose, and with the consequence stated. On a replacement device this
              button looks like the obvious way forward and is a trap: it mints a *new* random token that Azure
              will reject, while orphaning every backup the old six words still open (C57). */}
          <p data-testid="care-backup-mint-caution">{de.mintCaution}</p>
          <CareButton
            id="care-backup:create"
            label={busy ? '…' : de.backupSecretsCreate}
            onPress={() => void mint()}
          />
        </>
      ) : null}
    </section>
  );
}
